'use client';

import React, { HTMLAttributes, useEffect } from 'react';

import { Card } from '../Card';
import { Locale } from '../../../i18n.config';
import { getTranslation } from '@/lib/utils';
import { Experience, ExperienceTranslation } from '@/services/experienceTypes';
import useLocalStorage from '@/hooks/useLocalStorage';
import { ShortList } from '../CategorySections/ExperienceType';
import { useCurrency } from '@/contexts/currencyContext';
import { toShortListMedia } from '@/utils/shortlistUtils';

import parser from 'html-react-parser';

interface IExperienceCarouselItemProps extends HTMLAttributes<HTMLDivElement> {
  lang: Locale;
  experience: Experience;
}

const getCoverMedia = (exp: Experience) =>
  exp.media.find((m) => m.type === 'cover') ?? exp.media[0];

const paths = {
  en: '/sicily/experiences',
  it: '/sicilia/esperienze',
  de: '/sizilien/erfahrungen',
  fr: '/sicile/experiences',
};

export const ExperienceCarouselItem: React.FC<IExperienceCarouselItemProps> = ({
  lang,
  experience,
}) => {
  const [favourites, setFavourites] = useLocalStorage(
    'experience',
    [] as ShortList[],
  );

  const { currency } = useCurrency();

  useEffect(() => {
    const storedFavourites = localStorage.getItem('experience');
    if (storedFavourites) {
      try {
        setFavourites(JSON.parse(storedFavourites));
      } catch (error) {
        if (error instanceof Error) {
          console.log(`JSPERROR:103:${error.message}`);
        } else {
          console.log('JSPERROR:103', error);
        }
      }
    }
    //eslint-disable-next-line
  }, []);

  const toggleFavorite = (exp: Experience) => {
    const isFavourite = favourites.some(
      (item: { id: number }) => item.id === exp.id,
    );

    const titleSlugs = exp.experienceTranslation.map((item) => ({
      [item.locale]: item.slug,
      title: item.title,
    }));

    if (isFavourite) {
      const updatedFavourites = favourites.filter(
        (item: { id: number }) => item.id !== exp.id,
      );
      setFavourites(updatedFavourites);
    } else {
      let firstPrice = null;

      exp.optionMaps.some((option) => {
        const price = option.prices.find((price) => price.price);
        if (price) {
          firstPrice = price.price;
          return true;
        }
      });

      const shortListInfos: ShortList = {
        id: exp.id,
        title: translation.title,
        titleSlugs: titleSlugs,
        categoryId: exp.macroCategory.id,
        media: toShortListMedia(getCoverMedia(exp)),
        zone: {
          code: exp.zone ? exp.zone.code : '',
        },
        price: Number(firstPrice),
        currency: currency.value,
        city: exp.city,
        duration: exp.duration,
        languagens: exp.languages,
        minGuests: exp.minParticipants,
        maxGuests: exp.maxParticipants,
      };

      const updatedFavourites = [...favourites, shortListInfos];
      setFavourites(updatedFavourites);
    }
  };

  const translation = experience.experienceTranslation.find((item) => {
    if (item.locale === lang) {
      return item;
    }
  }) as ExperienceTranslation;

  const macroCategory = experience.macroCategory.translation?.find(
    (item) => item.locale === lang,
  );

  const href = `${paths[lang]}/${macroCategory?.slug}/${translation.slug}`;
  const coverMedia = getCoverMedia(experience);

  return (
    <Card
      key={experience.id}
      cardMedia={{ media: coverMedia, filterName: 'vertical_slider_webp' }}
      hasFavouriteButton={true}
      onClickFavourite={() => toggleFavorite(experience)}
      isFavourite={favourites.some(
        (item: { id: number }) => item.id === experience.id,
      )}
      href={href}
      hasSubtitle
      hoverContent={
        <div className="text-base text-center w-full box-border whitespace-normal leading-[1.6]">
          {parser(translation.description)}
        </div>
      }
      customStyle="flex-auto !w-4/5 desktop:!w-[calc(100%-15px)] mx-auto desktop:mr-0 desktop:ml-[15px] flex mx-auto h-full before:h-[40%] before:bg-home-gradient hover:mb-5 hover:-mt-5"
      sizes="(min-width: 640px) 50vw, (min-width: 800px) 33vw, (min-width: 1200px) 25vw, 100vw">
      <div className="flex flex-row items-center h-fit">
        <div className="flex flex-col flex-1">
          <p className="text-subtitle text-center w-full box-border whitespace-normal">
            <strong>
              {getTranslation(experience.experienceTranslation, lang).title}
            </strong>
          </p>
        </div>
      </div>
    </Card>
  );
};
